// =============================================================================
// CSV — importação e exportação.
// O alvo é o Excel em português: separador ';' e BOM UTF-8 na saída, senão
// "Autopeças" vira "AutopeÃ§as" ao abrir o arquivo exportado.
//
// As duas pontas têm uma regra de segurança cada:
//  * na SAÍDA, campo que começa com fórmula é neutralizado — quem digita o
//    texto (cliente da venda, descrição da peça, observação do caixa) é o
//    vendedor, e quem abre o arquivo no Excel é o dono;
//  * na ENTRADA, arquivo malformado FALHA com o número da linha em vez de
//    engolir linhas em silêncio — no mutirão de cadastro, perder peça sem
//    aviso é pior que recusar o arquivo.
// =============================================================================

/** Detecta o separador olhando a primeira linha (';' do Excel pt-BR vs ','). */
function detectarSeparador(primeiraLinha: string): string {
  const candidatos = [';', ',', '\t'];
  let melhor = ';';
  let maior = -1;
  for (const c of candidatos) {
    const n = primeiraLinha.split(c).length;
    if (n > maior) { maior = n; melhor = c; }
  }
  return melhor;
}

/** Uma linha lida, com o número FÍSICO dela no arquivo — é o que vai na mensagem de erro. */
interface LinhaCsv {
  numero: number;
  campos: string[];
}

/**
 * Leitura bruta. Duas diferenças em relação a um parser ingênuo, ambas nascidas
 * do jeito que autopeça escreve: 'Disco 12" ventilado'.
 *   1. Aspa só ABRE campo quando é o primeiro caractere dele. No meio do texto
 *      é polegada, não delimitador.
 *   2. Aspa aberta e nunca fechada vira ERRO com o número da linha. Antes,
 *      engolia o resto do arquivo num campo só e a importação seguia feliz.
 */
function lerLinhas(texto: string): LinhaCsv[] {
  const limpo = texto.replace(/^﻿/, '').replace(/\r\n?/g, '\n');
  const fimDaPrimeira = limpo.indexOf('\n');
  const sep = detectarSeparador(limpo.slice(0, fimDaPrimeira === -1 ? undefined : fimDaPrimeira));

  const linhas: LinhaCsv[] = [];
  let campo = '';
  let linha: string[] = [];
  let dentroDeAspas = false;

  let numeroFisico = 1;        // linha corrente no arquivo
  let inicioDoRegistro = 1;    // onde o registro corrente começou (campo multilinha)
  let aspaAbertaNaLinha = 0;   // para apontar a aspa culpada

  for (let i = 0; i < limpo.length; i++) {
    const c = limpo[i]!;

    if (dentroDeAspas) {
      if (c === '"') {
        if (limpo[i + 1] === '"') { campo += '"'; i++; }
        else dentroDeAspas = false;
      } else {
        if (c === '\n') numeroFisico++;
        campo += c;
      }
      continue;
    }

    if (c === '"' && campo === '') { dentroDeAspas = true; aspaAbertaNaLinha = numeroFisico; continue; }
    if (c === sep) { linha.push(campo); campo = ''; continue; }
    if (c === '\n') {
      linha.push(campo);
      linhas.push({ numero: inicioDoRegistro, campos: linha });
      linha = []; campo = '';
      numeroFisico++;
      inicioDoRegistro = numeroFisico;
      continue;
    }
    campo += c;
  }

  if (dentroDeAspas) {
    throw new Error(
      `Linha ${aspaAbertaNaLinha}: aspas abertas e nunca fechadas — se alguma descrição tem `
      + 'polegada ("), escreva 12 pol ou coloque o campo inteiro entre aspas duplicando a de dentro '
      + '("Disco 12"" ventilado"). O arquivo não foi importado.',
    );
  }
  if (campo || linha.length) {
    linha.push(campo);
    linhas.push({ numero: inicioDoRegistro, campos: linha });
  }

  return linhas.filter((l) => l.campos.some((c) => c.trim() !== ''));
}

/** Parser com suporte a aspas duplas e quebra de linha dentro do campo. */
export function parseCsv(texto: string): string[][] {
  return lerLinhas(texto).map((l) => l.campos);
}

/** NFD separa a letra do acento; a faixa apagada são só os acentos soltos. */
const ACENTOS = /[\u0300-\u036f]/g;

/** Normalização do cabeçalho: 'Preço Balcão' → 'preco_balcao'. */
function chaveDe(coluna: string): string {
  return coluna.trim().toLowerCase()
    .normalize('NFD').replace(ACENTOS, '')
    .replace(/\s+/g, '_');
}

/**
 * Linhas viram objetos com as chaves do cabeçalho, junto do número da linha no
 * arquivo — a tela de importação precisa dele para dizer ONDE deu errado.
 */
export function parseCsvLinhas(texto: string): Array<{ numero: number; dados: Record<string, string> }> {
  const linhas = lerLinhas(texto);
  if (linhas.length < 2) return [];

  const cabecalho = linhas[0]!.campos.map(chaveDe);

  return linhas.slice(1).map(({ numero, campos }) => {
    // Coluna a MAIS com conteúdo seria descartada em silêncio; quase sempre é
    // um ';' solto dentro de uma descrição, e o dado perdido é justamente o
    // preço. Coluna a menos continua virando string vazia (Excel corta a
    // cauda vazia da linha o tempo todo, e isso não perde nada).
    const excedente = campos.slice(cabecalho.length).filter((c) => c.trim() !== '');
    if (excedente.length > 0) {
      throw new Error(
        `Linha ${numero}: ${campos.length} colunas para um cabeçalho de ${cabecalho.length}. `
        + `Sobrou "${excedente[0]}" — provavelmente há um separador dentro de um campo sem aspas. `
        + 'O arquivo não foi importado.',
      );
    }

    const dados: Record<string, string> = {};
    cabecalho.forEach((chave, i) => { dados[chave] = (campos[i] ?? '').trim(); });
    return { numero, dados };
  });
}

/** Linhas viram objetos com as chaves do cabeçalho, normalizadas. */
export function parseCsvObjetos(texto: string): Array<Record<string, string>> {
  return parseCsvLinhas(texto).map((l) => l.dados);
}

// Excel e Google Sheets avaliam como FÓRMULA toda célula que começa com um
// destes. TAB e CR entram na lista porque o Excel os come antes de decidir.
const COMECA_FORMULA = /^[=+\-@\t\r]/;

// ...menos quando é número: '-12,50' e '-1.500' são valor negativo legítimo das
// colunas de dinheiro, e virar texto os tiraria de qualquer soma na planilha.
const NUMERO = /^-?\d{1,3}(\.\d{3})*(,\d+)?$|^-?\d+([.,]\d+)?$/;

/**
 * Gera CSV com BOM e ';' — abre direto no Excel pt-BR sem assistente.
 *
 * O apóstrofo à frente do campo perigoso é a neutralização escolhida: é o
 * marcador que o próprio Excel usa para "isto é texto", ele o consome ao abrir
 * (não aparece na célula) e sobrevive ao round-trip do nosso parser. Aspas de
 * CSV NÃO servem: o Excel desfaz o quoting e depois avalia a fórmula.
 */
export function paraCsv(colunas: string[], linhas: Array<Array<string | number | null>>): string {
  const escapar = (v: string | number | null): string => {
    const s = v === null || v === undefined ? '' : String(v);
    const seguro = COMECA_FORMULA.test(s) && !NUMERO.test(s) ? `'${s}` : s;
    // O \r entra no quoting porque um CR solto no meio do campo quebraria a
    // linha do CSV ao ser lido de volta.
    return /[";\n\r]/.test(seguro) ? `"${seguro.replace(/"/g, '""')}"` : seguro;
  };
  const corpo = [colunas, ...linhas].map((l) => l.map(escapar).join(';')).join('\r\n');
  return `﻿${corpo}`;
}

/** Dispara o download no navegador. */
export function baixarCsv(nomeArquivo: string, conteudo: string): void {
  const blob = new Blob([conteudo], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeArquivo;
  a.click();
  URL.revokeObjectURL(url);
}
